import type { AccessPermission } from './access-permissions';
import type { BUID } from './BUID';
import type { UserOrganisation } from './organisation-membership';
import { Md5 } from 'ts-md5';

export interface UserProfileSettings {
  phoneNumber: string;
  mfaMethod: 'SMS_MFA' | 'EMAIL_OTP' | 'TOTP';
}

// Identity-level UI preferences; an absent value means no choice made yet
export interface UiDefaults {
  newTransactionExperience?: boolean;
}

export interface UserProfile {
  buids: BUID[];
  grants: { action: string; resource: string }[];
  organisationId: string;
  email: string;
  name: string;
  accessLevel: number;
  accessLevelText: string;
  userId: string;
  createdAt: Date;
  lastUpdated: Date;
  lastLoggedIn: Date | null;
  deletedAt: Date;
  enabled: boolean;
  partnerId: string;
  organisationName: string;
  accessPermissions: AccessPermission[];
  organisations?: UserOrganisation[];
  uiDefaults?: UiDefaults;
}

export class ExtendedUserProfile implements UserProfile {
  buids: BUID[];
  grants: { action: string; resource: string }[];
  organisationId: string;
  email: string;
  name: string;
  accessLevel: number;
  accessLevelText: string;
  userId: string;
  picture: string;
  createdAt: Date;
  lastUpdated: Date;
  lastLoggedIn: Date | null;
  deletedAt: Date;
  enabled: boolean;
  partnerId: string;
  organisationName: string;
  accessPermissions: AccessPermission[];
  organisations?: UserOrganisation[];
  uiDefaults?: UiDefaults;

  constructor() {
    this.buids = [];
    this.grants = [];
    this.organisationId = '';
    this.email = '';
    this.name = '';
    this.accessLevel = 0;
    this.accessLevelText = '';
    this.userId = '';
    this.createdAt = new Date();
    this.lastUpdated = new Date();
    this.lastLoggedIn = null;
    this.deletedAt = new Date();
    this.enabled = true;
    this.partnerId = '';
    this.organisationName = '';
    this.accessPermissions = [];
  }

  public Build(profile: UserProfile): ExtendedUserProfile {
    this.buids = profile.buids;
    this.grants = profile.grants;
    this.organisationId = profile.organisationId;
    this.email = profile.email;
    this.name = profile.name;
    this.accessLevel = profile.accessLevel;
    this.accessLevelText = profile.accessLevelText;
    this.userId = profile.userId;
    this.createdAt = profile.createdAt;
    this.lastUpdated = profile.lastUpdated;
    this.lastLoggedIn = profile.lastLoggedIn ? profile.lastLoggedIn : null;
    this.deletedAt = profile.deletedAt;
    this.enabled = profile.enabled;
    this.partnerId = profile.partnerId;
    this.organisationName = profile.organisationName;
    this.accessPermissions = profile.accessPermissions;
    this.organisations = profile.organisations;
    this.uiDefaults = profile.uiDefaults;

    const address = String(this.email).trim().toLowerCase();
    const hash = Md5.hashStr(address);
    this.picture = `https://www.gravatar.com/avatar/${hash}?s=200&d=identicon&r=g`;
    return this;
  }


  // returns if the grant is active or not after toggle
  public ToggleResource(buid: BUID, grant: string): boolean {
    const existingRecord = this.grants.findIndex(g => g.resource === buid.id && g.action === grant);
    if (existingRecord > -1) {
      this.grants.splice(existingRecord, 1);
      return false;
    } else {
      this.grants.push({ action: grant, resource: buid.id });
      return true;
    }
  }

  public HasResource(buid: BUID, grant: string): boolean {
    return this.grants.findIndex(g => g.resource === buid.id && g.action === grant) > -1
  }
}
